/**
 * ApartmentSync Block
 *
 * Section block that renders the ApartmentSync (Repli360 / RRAC) floorplan
 * widget. Loading the script is delegated to useRRACScript, which is shared
 * with apartment-sync-map and the global apartment-sync-loader. If the
 * loader is on the page, this block hooks into it; otherwise it self-loads
 * (fallback) so existing pages keep working.
 *
 * Requires two per-property IDs stored as custom fields:
 *   - javascript_id: encoded token used in the script URL path
 *   - config_id: site identifier (baked into the loaded script as `site_id`)
 */

import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';
import { useRRACScript } from '../../hooks/useRRACScript';

function ApartmentSyncComponent(_props: BlockProps) {
  const javascriptId = useBlockToken('javascript_id', {
    defaultField: 'custom_apartmentsync_javascript_id',
  });
  const configId = useBlockToken('config_id', {
    defaultField: 'custom_apartmentsync_config_id',
  });
  const template = useBlockToken('template', { defaultValue: '' });

  useRRACScript({
    javascriptId: javascriptId || null,
    configId: configId || null,
    role: 'consumer',
  });

  if (!javascriptId) return null;

  const dataAttrs: Record<string, string> = {};
  if (template) dataAttrs['data-template'] = template;

  return (
    <section
      style={{
        background: 'var(--vibe-color-background, #ffffff)',
        color: 'var(--vibe-color-text, #333333)',
      }}
    >
      <div
        className="rrac_script"
        style={{ background: '#ffffff', color: '#333333' }}
        {...dataAttrs}
      />
    </section>
  );
}

const contentSchema: ComponentContentSchema = {
  slot: 'ApartmentSync',
  displayName: 'ApartmentSync Floorplans',
  tokens: {
    javascript_id: {
      label: 'JavaScript ID',
      type: 'text',
      defaultField: 'custom_apartmentsync_javascript_id',
      suggestedFields: ['custom_apartmentsync_javascript_id'],
      description:
        'Encoded token for the ApartmentSync script URL. Found in the Repli360 admin dashboard.',
      required: true,
    },
    config_id: {
      label: 'Config ID (Site ID)',
      type: 'text',
      defaultField: 'custom_apartmentsync_config_id',
      suggestedFields: ['custom_apartmentsync_config_id'],
      description:
        'ApartmentSync site identifier. Baked into the loaded script; stored here for reference.',
    },
    template: {
      label: 'Template Override',
      type: 'text',
      defaultValue: '',
      description:
        'Optional ApartmentSync template identifier (sets data-template on the container).',
    },
  },
};

export const apartmentSyncBlock: BlockDefinition = {
  id: 'apartment-sync',
  name: 'ApartmentSync Floorplans',
  description:
    'Interactive floorplan listings powered by ApartmentSync (Repli360). Displays unit availability, pricing, and filtering.',
  category: 'Integrations',
  placement: 'section',
  defaultScope: 'page',
  component: ApartmentSyncComponent,
  contentSchema,
  tags: ['apartmentsync', 'repli360', 'rrac', 'floorplans', 'availability', 'units', 'pms'],
};
